import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { InputColor } from "design-system/molecules/InputColor"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { AdminOnly } from "modules/shared/components/AdminOnly"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { GetWidgetConfigurationResponseData } from "schema"
import z from "zod"
import { WidgetConfigurationFormType } from ".."
import { LocalizedTexts } from "./LocalizedTexts"

type WidgetAppearanceProps = {
  form: UseFormReturn<WidgetConfigurationFormType>
}

type ThemeEnum = z.infer<typeof GetWidgetConfigurationResponseData>["theme"]

export const WidgetAppearance = ({ form }: WidgetAppearanceProps) => {
  const { t } = useTranslation()
  const { companyDescription, theme } = form.watch()
  const appearanceOptions = [
    {
      label: t("campaigns.templates.widget.config.appearance.theme.options.default"),
      value: "default",
    },
    {
      label: t("campaigns.templates.widget.config.appearance.theme.options.simple"),
      value: "simple",
    },
  ]

  return (
    <div className="flex flex-col gap-8">
      <div>
        <Label className="mb-4 block">
          {t("campaigns.templates.widget.config.appearance.theme.label")}
        </Label>
        <div className="flex flex-wrap items-center gap-4">
          <FormField
            control={form.control}
            name="theme"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    value={field.value}
                    onValueChange={(value) => form.setValue(field.name, value as ThemeEnum)}
                  >
                    <SelectTrigger className="w-fit min-w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {appearanceOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {theme === "default" && (
            <FormField
              control={form.control}
              name="hideCompanyLogo"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormLabel>
                    {t("campaigns.templates.widget.config.appearance.hideCompanyLogo")}
                  </FormLabel>
                  <FormControl>
                    <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 gap-y-8 items-start">
        <div className="col-span-1">
          <Label className="mb-4 block">
            {t("campaigns.templates.widget.config.appearance.companyBubble")}
          </Label>
          <div className="flex flex-col gap-2 pl-4 border-l">
            <FormField
              control={form.control}
              name="assistant.backgroundColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-muted-foreground">
                    {t("campaigns.templates.widget.config.appearance.background")}
                  </FormLabel>
                  <FormControl>
                    <InputColor {...field} required placeholder="#3C96B2" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="assistant.color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-muted-foreground">
                    {t("campaigns.templates.widget.config.appearance.color")}
                  </FormLabel>
                  <FormControl>
                    <InputColor {...field} required placeholder="#FFF" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="col-span-1">
          <Label className="mb-4 block">
            {t("campaigns.templates.widget.config.appearance.quickAction")}
          </Label>
          <div className="flex flex-col gap-2 pl-4 border-l">
            <FormField
              control={form.control}
              name="actions.backgroundColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-muted-foreground">
                    {t("campaigns.templates.widget.config.appearance.background")}
                  </FormLabel>
                  <FormControl>
                    <InputColor {...field} required placeholder="#B3D2DC" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="actions.color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-muted-foreground">
                    {t("campaigns.templates.widget.config.appearance.color")}
                  </FormLabel>
                  <FormControl>
                    <InputColor {...field} required placeholder="#FFF" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="actions.borderColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-muted-foreground">
                    {t("campaigns.templates.widget.config.appearance.borderColor")}
                  </FormLabel>
                  <FormControl>
                    <InputColor {...field} required placeholder="#3C96B2" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="col-span-1">
          <Label className="mb-4 block">
            {t("campaigns.templates.widget.config.appearance.customerBubble")}
          </Label>
          <div className="flex flex-col gap-2 pl-4 border-l">
            <FormField
              control={form.control}
              name="user.backgroundColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-muted-foreground">
                    {t("campaigns.templates.widget.config.appearance.background")}
                  </FormLabel>
                  <FormControl>
                    <InputColor {...field} required placeholder="#3C96B2" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user.color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-muted-foreground">
                    {t("campaigns.templates.widget.config.appearance.color")}
                  </FormLabel>
                  <FormControl>
                    <InputColor {...field} required placeholder="#FFF" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="col-span-1">
          <Label className="mb-4 block">
            {t("campaigns.templates.widget.config.appearance.chat")}
          </Label>
          <div className="flex flex-col gap-2 pl-4 border-l">
            <FormField
              control={form.control}
              name="dialog.backgroundColor"
              render={({ field }) => (
                <FormItem className="col-span-1">
                  <FormLabel className="text-muted-foreground">
                    {t("campaigns.templates.widget.config.appearance.background")}
                  </FormLabel>
                  <FormControl>
                    <InputColor {...field} required placeholder="#FFFFFF" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="col-span-1">
          <Label className="mb-4 block">
            {t("campaigns.templates.widget.config.appearance.launcher")}
          </Label>
          <div className="flex flex-col gap-2 pl-4 border-l">
            <FormField
              control={form.control}
              name="bubble.backgroundColor"
              render={({ field }) => (
                <FormItem className="col-span-1">
                  <FormLabel>
                    {t("campaigns.templates.widget.config.appearance.background")}
                  </FormLabel>
                  <FormControl>
                    <InputColor {...field} required placeholder="#B3D2DC" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bubble.color"
              render={({ field }) => (
                <FormItem className="col-span-1">
                  <FormLabel>
                    {t("campaigns.templates.widget.config.appearance.launcherColor")}
                  </FormLabel>
                  <FormControl>
                    <InputColor {...field} required placeholder="#FFFFFF" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <FormField
          control={form.control}
          name="companyDescription.display"
          render={({ field }) => (
            <FormItem className="flex items-center gap-2">
              <FormLabel>
                {t("campaigns.templates.widget.config.appearance.showCompanyDescription")}
              </FormLabel>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={(value) => form.setValue(field.name, value)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {companyDescription.display && (
          <LocalizedTexts form={form} name="companyDescription.texts" />
        )}
      </div>
      <AdminOnly>
        <div className="flex flex-col gap-2">
          <FormField
            control={form.control}
            name="hasWatermark"
            render={({ field }) => (
              <FormItem className="flex items-center gap-2">
                <FormLabel>
                  {t("campaigns.templates.widget.config.appearance.showWatermark")}
                </FormLabel>
                <FormControl>
                  <Switch
                    {...field}
                    defaultChecked={!!form.watch("hasWatermark")}
                    value={field.value.toString()}
                    onCheckedChange={(value) => form.setValue(field.name, value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </AdminOnly>
    </div>
  )
}
